import React, { useEffect } from 'react';
import useSetState from '../../hooks/useSetState';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ShowForPermission from '../../components/ShowForPermission';
import ENUMS from '../../enums';
import Table from '../../components/table';
import { citiesListColumns } from '../../enums/columns/citiesList';
import { getCitiesList } from '../../redux/cities/actions';
import cookies from 'js-cookie';
import submitForm from '../../utils/submitForm';

const CitiesAndRegions = () => {
  let dispatch = useDispatch();

  const [controls, setControls] = useSetState({
    sort: { sortType: 'desc', sortBy: 'name', sortQuery: '', filterQuery: '' },
    pageNumber: 1,
    pageSize: 10,
    name: '',
    cityCode: '',
  });

  const onChangeValue = (controleName, value) => {
    setControls({ [controleName]: value });
  };

  const { citiesList, citiesListTotalRecords, getCitiesListLoading } = useSelector(state => ({
    citiesList: state.citiesReducer.citiesList,
    getCitiesListLoading: state.citiesReducer.getCitiesListLoading,
    citiesListTotalRecords: state.citiesReducer.citiesListTotalRecords,
  }));

  const buildSearchQuery = (pageNumber, pageSize) => {
    if (!isNaN(pageNumber) && !isNaN(pageSize)) {
      let query = `pageNumber=${pageNumber}&pageSize=${pageSize}`;
      query += `&sort=${controls.sort.sortQuery || 'startDate-desc'}`;
      if (controls.sort.filterQuery) {
        query += '&' + controls.sort.filterQuery;
      }
      return query;
    }
  };

  const onPageChanged = pageNumber => {
    onChangeValue('pageNumber', pageNumber);
    const query = buildSearchQuery(pageNumber, controls.pageSize);
    dispatch(getCitiesList(query));
  };

  const sortTable = (columnName) => {
    const sortType = controls.sort.sortType === 'asc' ? 'desc' : 'asc';
    onChangeValue('sort', { ...controls.sort, sortType, sortBy: columnName });
  };

  const filterAndSort = () => {
    let searchQuery = buildSearchQuery(1, controls.pageSize);
    dispatch(getCitiesList(searchQuery));
  };

  const resetForm = () => {
    onChangeValue('pageNumber', 1);
    onChangeValue('name', '');
    onChangeValue('cityCode', '');
    onChangeValue('sort', { ...controls.sort, filterQuery: '' });
    let query = `pageNumber=${1}&pageSize=${controls.pageSize}`;
    query += `&sort=${controls.sort.sortQuery || 'startDate-desc'}`;
    dispatch(getCitiesList(query));
  };

  const onSubmit = event => {
    event.preventDefault();
    const queryString = submitForm(event,
      controls.name,
      controls.cityCode,);
    onChangeValue('sort', { ...controls.sort, filterQuery: queryString });
  };

  useEffect(() => {
    let query = `pageNumber=${controls.pageNumber}&pageSize=${controls.pageSize}`;
    dispatch(getCitiesList(query))
    cookies.remove('cityId');
  }, []);

  useEffect(() => {
    const sortQuery = `${controls.sort.sortBy}-${controls.sort.sortType}`;
    onChangeValue('sort', { ...controls.sort, sortQuery });
  }, [controls.sort.sortType]);

  useEffect(() => {
    if (controls.sort.sortQuery !== '') {
      onChangeValue('pageNumber', 1);
      filterAndSort();
    }
  }, [controls.sort.sortQuery]);

  useEffect(() => {
    if (controls.sort.filterQuery !== '') {
      onChangeValue('pageNumber', 1);
      filterAndSort();
    }
  }, [controls.sort.filterQuery]);

  return (
    <section>
      <div className='col-12 d-flex justify-content-between align-items-center'>
        <h3 className='text-right text-muted py-4'>
          {locales('manage.cities.and.regions')}
        </h3>
        <div className="form-actions">
          <ShowForPermission permission={ENUMS.PERMISSIONS.values.ADD_NEW_CITY}>
            <Link to="/add-or-edit-city"
              className="btn btn-primary mx-1">
              <i className="fa fa-plus mx-1" />
              {locales('add.new.city')}
            </Link>
          </ShowForPermission>
        </div>
      </div>

      <section className='city-filters'>
        <form
          onSubmit={event => onSubmit(event)}
          id="filterForm"
          className="card card my-4">

          <header className="text-center card-header">
            <h5 className="font-italic text-muted">{locales('titles.filter')}</h5>
          </header>

          <main className="card-body">
            <div className="d-flex row flex-sm-column col-12 flex-md-row justify-content-between px-1 mx-1">
              <div className={`d-flex col-lg-6 col-md-12 col-sm-12 align-items-center`}>
                <ShowForPermission
                  permission={ENUMS.PERMISSIONS.values.VIEW_CITIES_LIST}>
                  <label className="rsee-form-label text-right">
                    {locales('tables.cityName')}
                  </label>
                  <div className="full-width">
                    <input
                      className="form-control col-12"
                      type="text"
                      id="name"
                      name="name"
                      placeholder={locales('tables.cityName')}
                      onChange={e => onChangeValue('name', e.target.value)}
                      value={controls.name}
                    />
                  </div>
                </ShowForPermission>
              </div>

              <div className={`d-flex col-lg-6 col-md-12 col-sm-12 align-items-center`}>
                <ShowForPermission
                  permission={ENUMS.PERMISSIONS.values.VIEW_CITIES_LIST}>
                  <label className="rsee-form-label text-right">
                    {locales('city.code')}
                  </label>
                  <div className="full-width">
                    <input
                      className="form-control col-12"
                      type="text"
                      id="cityCode"
                      name="cityCode"
                      placeholder={locales('city.code')}
                      onChange={e => onChangeValue('cityCode', e.target.value)}
                      value={controls.cityCode}
                    />
                  </div>
                </ShowForPermission>
              </div>
            </div>

            <div className="col-12 d-flex justify-content-end p-0 my-3">
              <button type="button"
                onClick={resetForm}
                className="btn btn-danger w-5 mx-2">
                {locales('fields.reset')}
              </button>
              <button type="submit" className="btn btn-success w-5 mx-2">
                {locales('fields.applyFilters')}
              </button>
            </div>

          </main>
        </form>
      </section >

      <div className='bg-white p-2'>
        <div className='bg-white p-2'>
          <div className="panel-heading">
            <i className="fas fa-list-alt px-1" />
            {locales('cities.list')}
          </div>
          <div className='table-responsive-lg'>
            <Table
              columns={citiesListColumns}
              dataSource={citiesList}
              loading={getCitiesListLoading}
              sort={{ type: controls.sort.sortType, action: sortTable }}
              pagination={{
                currentPage: controls.pageNumber,
                pageSize: controls.pageSize,
                totalCount: citiesListTotalRecords || 0,
                onPageChange: onPageChanged,
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default CitiesAndRegions;